import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ImageUpload from '@/components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = { nis: '', name: '', foto: '', halaqah_id: '', kelas: '', tahun_masuk: '', tanggal_lahir: '', alamat: '', jenis_kelamin: '' };

export default function ManajemenSantri() {
  const [santri, setSantri] = useState([]);
  const [halaqah, setHalaqah] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    const [s, h] = await Promise.all([
      axios.get(`${API}/santri`).then(r => r.data).catch(() => []),
      axios.get(`${API}/halaqah`).then(r => r.data).catch(() => []),
    ]);
    setSantri(s); setHalaqah(h);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/santri/${editId}`, form);
        toast.success('Santri diperbarui');
      } else {
        await axios.post(`${API}/santri`, form);
        toast.success('Santri ditambahkan');
      }
      setShowForm(false); setEditId(null); setForm(emptyForm);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const handleEdit = (s) => {
    setForm({ nis: s.nis, name: s.name, foto: s.foto || '', halaqah_id: s.halaqah_id || '',
              kelas: s.kelas || '',
              tahun_masuk: s.tahun_masuk || '', tanggal_lahir: s.tanggal_lahir || '',
              alamat: s.alamat || '', jenis_kelamin: s.jenis_kelamin || '' });
    setEditId(s.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus santri ini?')) return;
    await axios.delete(`${API}/santri/${id}`);
    toast.success('Santri dihapus');
    fetchAll();
  };

  const filtered = santri.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.nis?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">Data Santri</h1>
            <p className="text-sm text-gray-500">{santri.length} santri terdaftar</p>
          </div>
          <button
            data-testid="add-santri-btn"
            onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all duration-200"
          >
            <Plus size={16} /> Tambah Santri
          </button>
        </div>

        <div className="p-4 md:p-8">
          <input
            data-testid="santri-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cari nama atau NIS..."
            className="w-full sm:max-w-xs px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />

          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[540px]">
              <thead>
                <tr className="bg-emerald-50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">NIS</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">Nama</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">Halaqah</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">Tahun Masuk</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-emerald-700 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((s, i) => {
                  const hal = halaqah.find(h => h.id === s.halaqah_id);
                  return (
                    <motion.tr
                      key={s.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">{s.nis}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-emerald-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                            {s.foto ? (
                              <img src={s.foto} alt={s.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-emerald-700 font-bold text-xs">{s.name?.charAt(0)}</span>
                            )}
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-gray-800">{s.name}</span>
                            {s.kelas && (
                              <span className={`ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-md ${
                                {D:'bg-gray-100 text-gray-600',C:'bg-yellow-100 text-yellow-700',B:'bg-blue-100 text-blue-700',A:'bg-emerald-100 text-emerald-700',M:'bg-purple-100 text-purple-700'}[s.kelas] || 'bg-gray-100 text-gray-600'
                              }`}>{s.kelas}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{hal?.nama || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{s.tahun_masuk || '-'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`edit-santri-${s.id}`} onClick={() => handleEdit(s)}
                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors">
                            <Pencil size={14} />
                          </button>
                          <button data-testid={`delete-santri-${s.id}`} onClick={() => handleDelete(s.id)}
                            className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400 text-sm">Belum ada data santri</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>

        {/* Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">
                    {editId ? 'Edit Santri' : 'Tambah Santri'}
                  </h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                    <X size={18} />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">NIS *</label>
                      <input required value={form.nis} onChange={e => setForm(f => ({...f, nis: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nama Lengkap *</label>
                      <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Halaqah</label>
                    <select value={form.halaqah_id} onChange={e => setForm(f => ({...f, halaqah_id: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                      <option value="">-- Pilih Halaqah --</option>
                      {halaqah.map(h => <option key={h.id} value={h.id}>{h.nama}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Kelas</label>
                    <select value={form.kelas} onChange={e => setForm(f => ({...f, kelas: e.target.value}))}
                      data-testid="santri-kelas-select"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                      <option value="">-- Pilih Kelas --</option>
                      <option value="D">D - I'dad</option>
                      <option value="C">C - Uula</option>
                      <option value="B">B - Wustha</option>
                      <option value="A">A - Ulya</option>
                      <option value="M">M - Ali/Mahir</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tahun Masuk</label>
                      <input value={form.tahun_masuk} onChange={e => setForm(f => ({...f, tahun_masuk: e.target.value}))}
                        placeholder="2024" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Jenis Kelamin</label>
                      <select value={form.jenis_kelamin} onChange={e => setForm(f => ({...f, jenis_kelamin: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                        <option value="">-- Pilih --</option>
                        <option value="Laki-laki">Laki-laki</option>
                        <option value="Perempuan">Perempuan</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Alamat</label>
                    <textarea value={form.alamat} onChange={e => setForm(f => ({...f, alamat: e.target.value}))}
                      rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
                  </div>
                  <ImageUpload value={form.foto} onChange={v => setForm(f => ({...f, foto: v}))} label="Foto Santri" />
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                      Batal
                    </button>
                    <button data-testid="santri-form-submit" type="submit"
                      className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-semibold shadow-emerald transition-all duration-200 flex items-center justify-center gap-2">
                      <Check size={15} /> Simpan
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
